import { Component, Inject, OnInit, ViewChild, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxSpinnerService } from 'ngx-spinner';
import { ServicesService } from 'src/services/services.service';

export interface ScanRecord {
    srNo?: any;
    scan_id: number;
    qr_id: number;
    public_uid: string;
    user_id: number;
    user_name: string;
    user_mobile: string;
    user_email: string;
    // result_status: string;
    scan_date: string;
    device_type: string;
    os: string;
    // browser: string;
    device_name: string;
    total_scans_for_qr: number;
    serial_number: string;
}

export interface UserScan {
    srNo?: any;
    user_id: number;
    user_name: string;
    user_mobile: string;
    user_email: string;
    user_scan_count: number;
    unique_qrcodes_scanned: number;
    first_scan: string;
    last_scan: string;
}

@Component({
    selector: 'app-product-qr-scan-analytics',
    templateUrl: './product-qr-scan-analytics.component.html',
    styleUrls: ['./product-qr-scan-analytics.component.scss'],
    standalone: false
})
export class ProductQrScanAnalyticsComponent implements OnInit {
    private destroyRef = inject(DestroyRef);

    // Data
    scanRecords: ScanRecord[] = [];
    userScans: UserScan[] = [];
    summary: any = {};

    // Table datasources
    scansDataSource = new MatTableDataSource<ScanRecord & { srNo: number }>();
    usersDataSource = new MatTableDataSource<UserScan & { srNo: number }>();

    // Table configurations
    scansDisplayedColumns: string[] = [
        'srNo',
        'scan_date',
        'user_name',
        'user_mobile',
        'device_type',
        'os',
        // 'browser',
        'device_name',
        // 'result_status'
    ];

    usersDisplayedColumns: string[] = [
        'srNo',
        'user_name',
        'user_mobile',
        'user_scan_count',
        'unique_qrcodes_scanned',
        'first_scan',
        'last_scan'
    ];

    @ViewChild('scansPaginator') scansPaginator!: MatPaginator;
    @ViewChild('usersPaginator') usersPaginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    selectedTab = 0;
    loading = true;
    filterValue = '';

    constructor(
        private service: ServicesService,
        private spinner: NgxSpinnerService,
        @Inject(MAT_DIALOG_DATA) public data: { batch_id: number; product_title: string }
    ) { }

    ngOnInit() {
        this.loadScanAnalytics();
    }

    loadScanAnalytics() {
        this.spinner.show();
        this.loading = true;

        this.service.get_product_qr_scan_analytics(this.data.batch_id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res: any) => {
                    if (res?.flag && res?.data) {
                        this.scanRecords = res.data.scans || [];
                        this.userScans = res.data.user_scans || [];
                        this.summary = res.data.summary || {};

                        // Setup datasources
                        this.setupDataSources();
                    }
                    this.loading = false;
                    this.spinner.hide();
                },
                error: (err) => {
                    console.error('Error loading scan analytics:', err);
                    this.loading = false;
                    this.spinner.hide();
                }
            });
    }

    setupDataSources() {
        // Map scan records with index
        const scansWithIndex = this.scanRecords.map((scan, index) => ({
            ...scan,
            srNo: index + 1
        }));

        this.scansDataSource = new MatTableDataSource(scansWithIndex);
        this.scansDataSource.paginator = this.scansPaginator;
        this.scansDataSource.sort = this.sort;

        // Map user scans with index
        const usersWithIndex = this.userScans.map((user, index) => ({
            ...user,
            srNo: index + 1
        }));

        this.usersDataSource = new MatTableDataSource(usersWithIndex);
        this.usersDataSource.paginator = this.usersPaginator;
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;

        if (this.selectedTab === 0) {
            this.scansDataSource.filter = filterValue.trim().toLowerCase();
        } else {
            this.usersDataSource.filter = filterValue.trim().toLowerCase();
        }
    }

    onTabChange(event: any) {
        this.selectedTab = event.index;
    }

    getStatusColor(status: string): string {
        if (status === 'success') return 'success';
        if (status === 'failed') return 'warn';
        return 'info';
    }

    getDeviceIcon(deviceType: string): string {
        const type = deviceType.toLowerCase();
        if (type.includes('android')) return 'smartphone';
        if (type.includes('ios')) return 'smartphone';
        if (type.includes('windows')) return 'desktop_mac';
        if (type.includes('macos')) return 'desktop_mac';
        if (type.includes('linux')) return 'computer';
        return 'devices';
    }

    formatDate(date: string): string {
        if (!date) return '-';
        const d = new Date(date);
        return d.toLocaleString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    }

    downloadScanData() {
        const csv = this.generateCSV();
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `scan-analytics-${this.data.batch_id}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    generateCSV(): string {
        let csv = 'Scan Analytics Report\n';
        csv += `Product: ${this.data.product_title}\n`;
        csv += `Generated: ${new Date().toLocaleString()}\n\n`;

        csv += 'SUMMARY\n';
        csv += `Total Unique Users,${this.summary.total_unique_users || 0}\n`;
        csv += `Total QR Codes Scanned,${this.summary.total_unique_qrcodes || 0}\n`;
        csv += `Total Scans,${this.summary.total_scans || 0}\n`;
        csv += `Successful Scans,${this.summary.successful_scans || 0}\n`;
        csv += `Failed Scans,${this.summary.failed_scans || 0}\n\n`;

        csv += 'SCAN DETAILS\n';
        csv += 'Scan Date,User Name,User Mobile,Device Type,OS,Device Name,Status\n';
        this.scanRecords.forEach(scan => {
            csv += `"${scan.scan_date}","${scan.user_name || '-'}","${scan.user_mobile || '-'}","${scan.device_type}","${scan.os}","${scan.device_name}"\n`;
        });

        csv += '\nUSER SCAN SUMMARY\n';
        csv += 'User Name,User Mobile,Scan Count,Unique QR Codes,First Scan,Last Scan\n';
        this.userScans.forEach(user => {
            csv += `"${user.user_name || '-'}","${user.user_mobile || '-'}","${user.user_scan_count}","${user.unique_qrcodes_scanned}","${user.first_scan}","${user.last_scan}"\n`;
        });

        return csv;
    }
}
